import React from 'react';
import SessionAttendance from './SessionAttendance';
import { resolveSessionSchoolName } from '../utils/sessionSchoolName';

function formatSessionDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

export default function SessionDetail({
  session,
  currentUser,
  auditors = [],
  counselors = [],
  teachers = [],
  resources = [],
  getAutoResources,
  cohorts = [],
  schools = [],
  onClose,
  onEdit,
  onCancel,
  onStatusChange,
  onManageAssignments,
  showManageAssignments,
  onManageAssignmentsClose,
  onManageAssignmentsSaved,
  showToast,
}) {
  const [statusBusy, setStatusBusy] = React.useState(false);
  const isPA = currentUser?.role === 'program_administrator';
  const statusKey = String(session.status || '').toLowerCase();
  const isPendingAssignment = (session.auditors || []).length === 0 || !session.counselorId;

  // Prefer live school directory when API still returns bootstrap "School #N".
  const resolvedSchoolName = resolveSessionSchoolName(session, schools, cohorts);
  // Edit + cancel only while session can still be rescheduled (planned/live).
  const canModify = isPA && (statusKey === 'planned' || statusKey === 'live');
  // Assignments stay editable for PA after completion (past-session auditor/counselor fixes).
  // Cancelled sessions stay locked.
  const canManageAssignments = isPA
    && typeof onManageAssignments === 'function'
    && statusKey !== 'cancelled';
  // PA may flip status even when cancelled/completed (ops recovery); cancel stays separate.
  const canChangeStatus = isPA && typeof onStatusChange === 'function' && statusKey !== 'cancelled';

  // Attendance cohort scope: single-cohort sessions use session.cohortId directly.
  // Group sessions span multiple cohorts — a SPOC/counselor only sees the
  // cohort(s) they're scoped to; PA sees all. Backend enforces this too.
  const attendanceCohortIds = session.groupId
    ? (session.groupCohortIds || []).filter((id) => {
        if (isPA) return true;
        const c = cohorts.find(x => x.id === id);
        if (!c) return false;
        if (currentUser?.role === 'spoc') {
          return String(c.spocId) === String(currentUser?.apiUser?.id) || String(c.spocId) === String(currentUser?.apiUser?.user_id);
        }
        return true; // counselor scope is enforced server-side; roster fetch 403s silently handled per-block
      })
    : (session.cohortId ? [session.cohortId] : []);

  const cohort = cohorts.find(c => c.id === session.cohortId);
  const spoc = cohort?.spocId
    ? (teachers.find(t => String(t.id) === String(cohort.spocId)) || null)
    : null;
  const isSelfSpoc = cohort?.spocId
    && (String(cohort.spocId) === String(currentUser?.apiUser?.id)
      || String(cohort.spocId) === String(currentUser?.apiUser?.user_id));
  const spocLabel = spoc ? spoc.name : (isSelfSpoc ? currentUser.name : (cohort?.spocName || null));

  const sessionAuditors = (session.auditorAssignments || []).map(a => {
    const aud = auditors.find(x => String(x.id) === String(a.auditorId));
    return { ...a, name: aud?.name || a.auditorName || `Auditor #${a.auditorId}` };
  });

  const counselor = session.counselorId
    ? counselors.find(c => String(c.id) === String(session.counselorId))
    : null;

  const sessionResources = (session.resources || [])
    .map(id => resources.find(r => String(r.id) === String(id)))
    .filter(Boolean);
  const displayedResources = sessionResources.length > 0
    ? sessionResources
    : (getAutoResources ? getAutoResources(session.sessionType, session.levelYear)
      .map(id => resources.find(r => String(r.id) === String(id)))
      .filter(Boolean) : []);

  const handleCancelClick = async () => {
    if (!window.confirm('Cancel this session? This action cannot be undone.')) return;
    try {
      await onCancel(session.id);
      onClose();
    } catch {
      // Error is already toasted by the parent; keep the detail view open.
    }
  };

  const handleStatusClick = async (nextStatus, label) => {
    if (!onStatusChange || statusBusy) return;
    const msg = nextStatus === 'completed'
      ? 'Mark this session as completed? Attendance and post-session flows unlock for completed sessions.'
      : nextStatus === 'live'
        ? 'Mark this session as live? Nominations cannot be edited while the session is live.'
        : `Set session status to ${label}?`;
    if (!window.confirm(msg)) return;
    setStatusBusy(true);
    try {
      await onStatusChange(session.id, nextStatus);
    } catch {
      // Parent toasts
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <button className="btn-back-link" onClick={onClose} style={{ marginBottom: '16px' }}>
        ← Back to Sessions List
      </button>
      <div className="dashboard-title-row" style={{ marginBottom: '24px' }}>
        <div>
          <h1 className="dashboard-title">Session Details</h1>
          <p className="dashboard-subtitle">
            {session.sessionType} • {session.cohortName || session.groupName || '—'}
            {isPendingAssignment && (
              <span style={{ marginLeft: '10px', fontSize: '11px', background: '#fef3c7', color: '#b45309', padding: '2px 10px', borderRadius: '999px', fontWeight: 600, verticalAlign: 'middle' }}>
                Needs Assignment
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'flex-end' }}>
          {canChangeStatus && statusKey === 'planned' && (
            <button
              className="btn-primary-action"
              type="button"
              disabled={statusBusy}
              onClick={() => handleStatusClick('live', 'Live')}
            >
              Mark Live
            </button>
          )}
          {canChangeStatus && (statusKey === 'planned' || statusKey === 'live') && (
            <button
              className="btn-secondary-action"
              type="button"
              disabled={statusBusy}
              onClick={() => handleStatusClick('completed', 'Completed')}
              style={{ borderColor: '#10b981', color: '#047857' }}
            >
              Mark Completed
            </button>
          )}
          {canChangeStatus && statusKey === 'completed' && (
            <button
              className="btn-secondary-action"
              type="button"
              disabled={statusBusy}
              onClick={() => handleStatusClick('live', 'Live')}
            >
              Reopen as Live
            </button>
          )}
          {canChangeStatus && statusKey === 'live' && (
            <button
              className="btn-secondary-action"
              type="button"
              disabled={statusBusy}
              onClick={() => handleStatusClick('planned', 'Planned')}
            >
              Back to Planned
            </button>
          )}
          {canManageAssignments && (
            <button className="btn-secondary-action" onClick={onManageAssignments} style={{ borderColor: '#f59e0b', color: '#92400e' }}>
              Manage Assignments
            </button>
          )}
          {canModify && (
            <>
              <button className="btn-secondary-action" onClick={() => onEdit(session)}>Edit</button>
              <button
                className="btn-secondary-action"
                onClick={handleCancelClick}
                style={{ color: '#b91c1c', borderColor: '#fecaca' }}
              >
                Cancel Session
              </button>
            </>
          )}
        </div>
      </div>

      <div className="session-form-single-card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Status</div>
            <span className={`status-pill ${statusKey}`}>{session.status}</span>
            {isPA && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px', maxWidth: '220px', lineHeight: 1.35 }}>
                Auto: <strong>Live</strong> on session day; <strong>Completed</strong> 1 hour after the time slot ends (India; cron may lag ~15 min). PA can override anytime.
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>School</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>{resolvedSchoolName || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Cohort / Group</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>{session.cohortName || session.groupName || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Date</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>{formatSessionDate(session.scheduledAt)}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Time Slot</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>{session.timeSlot || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Meeting Link</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>
              {session.meetingLink ? (
                <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Join Room</a>
              ) : '—'}
            </div>
          </div>
        </div>

        {session.comments && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>Comments</div>
            <div style={{ background: 'var(--neutral-50)', padding: '14px 16px', borderRadius: '8px', fontSize: '13px', color: 'var(--neutral-700)', whiteSpace: 'pre-wrap', border: '1px solid var(--border-color)' }}>
              {session.comments}
            </div>
          </div>
        )}

        <div style={{ marginTop: '24px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>Auditors</div>
          {sessionAuditors.length === 0 ? (
            <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '13px' }}>No auditors assigned.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sessionAuditors.map(a => (
                <div
                  key={a.id || a.auditorId}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--neutral-50)', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                >
                  <span style={{ fontSize: '14px', color: 'var(--neutral-800)', fontWeight: 500 }}>{a.name}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Room {a.roomNumber ?? '—'} • {a.assignmentStatus || 'pending'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Counselor</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>{counselor ? counselor.name : '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>SPOC</div>
            <div style={{ fontSize: '14px', color: 'var(--neutral-800)' }}>{spocLabel || '—'}</div>
          </div>
        </div>

        <div style={{ marginTop: '24px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>Resources</div>
              {displayedResources.length === 0 ? (
            <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '13px' }}>No resources attached.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {displayedResources.map(res => (
                <div
                  key={res.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(1, 71, 162, 0.03)', border: '1px solid rgba(1, 71, 162, 0.18)', borderRadius: '6px' }}
                >
                  <span style={{ fontSize: '18px', lineHeight: 1 }}>
                    {res.fileType === 'ppt' ? '📊' : res.fileType === 'pdf' ? '📄' : res.fileType === 'mp4' ? '🎬' : '📎'}
                  </span>
                  <span style={{ flexGrow: 1, fontSize: '14px', color: 'var(--neutral-800)', fontWeight: 500 }}>{res.title}</span>
                  <span className="checklist-row-badge" style={{ margin: 0, flexShrink: 0 }}>{res.fileType.toUpperCase()}</span>
                  {res.viewUrl && (
                    <a href={res.viewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', textDecoration: 'underline' }}>Preview</a>
                  )}
                  {res.downloadUrl && (
                    <a href={res.downloadUrl} download style={{ fontSize: '13px', textDecoration: 'underline' }}>Download</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {attendanceCohortIds.map((cid) => (
          <SessionAttendance
            key={cid}
            session={session}
            currentUser={currentUser}
            cohortId={cid}
            cohortLabel={session.groupId ? (cohorts.find(c => c.id === cid)?.name || `Cohort #${cid}`) : null}
            showToast={showToast}
          />
        ))}
      </div>
    </div>
  );
}
